import { useEffect, useState, type FunctionComponent } from "react";
import { z } from "zod";
import { Form } from "../../../components/ui/form";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import Spinner from "../../../components/spinner.components";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "../../../hooks/use-toast";
import { FormInput } from "../../../components/form-components/form-input.components";
import { handleHttpError } from "../../../utils/utils";
import { useAuthenticationStore } from "../../../store/authentication.store";
import { useAdminSendOtpCommonHttp } from "../../../hooks/http/common/users/admin-send-otp.common.http";
import { useAdminVerifyOtpCommonHttp } from "../../../hooks/http/common/users/admin-verify-otp.common.http";
import { useAdminResendOtpCommonHttp } from "../../../hooks/http/common/users/admin-resend-otp.common.http";
import { useDashboardConfigCommonHttp } from "../../../hooks/http/common/users/dashboard-config.common.http";
import { useUserStore } from "../../../store/user.store";
import OTPVerificationForm from "../../../components/otp-verify-form.components";

const FormSchema = z.object({
  email: z.string().email({ message: "invalid email address" }),
});

export const LoginAuthCommonPartial: FunctionComponent = () => {
  const [otpSent, setOtpSent] = useState(false);

  const navigate = useNavigate();
  const { authenticationState, setAuthenticationState } =
    useAuthenticationStore();
  const { fetching: sendingOtp, execute: sendOtpHttp } =
    useAdminSendOtpCommonHttp();
  const { fetching: verifying, execute: verifyOtpHttp } =
    useAdminVerifyOtpCommonHttp();
  const { execute: resendOtpHttp } = useAdminResendOtpCommonHttp();
  const { fetching: fetchingConfig, execute: fetchConfig } =
    useDashboardConfigCommonHttp();
  const { login, setUser, logout, setBrands } = useUserStore();
  const { toast } = useToast();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: { email: "" },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (form.formState.errors) {
      const key = Object.keys(form.formState.errors)[0];
      if (key) {
        toast({
          content:
            form.formState.errors[key as keyof typeof form.formState.errors]
              ?.message || "",
          toastType: "error",
        });
      }
    }
  }, [form.formState.errors]);

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    const response = await sendOtpHttp({ body: { email: data.email } });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setAuthenticationState({ otp: response.data, email: data.email });
    setOtpSent(true);
  }

  async function onResend() {
    const response = await resendOtpHttp({
      body: { id: authenticationState?.otp?.id || "" },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    if (authenticationState) {
      setAuthenticationState({ ...authenticationState, otp: response.data });
    }
  }

  async function onVerify(pin: string) {
    if (!authenticationState?.otp?.id) {
      return toast({
        content: "Session expired, please restart",
        toastType: "error",
      });
    }

    const verifyResponse = await verifyOtpHttp({
      body: { id: authenticationState.otp.id, otp: pin },
    });
    if (!verifyResponse.success) {
      return handleHttpError(verifyResponse, toast);
    }

    const verifiedUser = verifyResponse.data.user;
    await login(verifyResponse.data.token, verifiedUser, null);

    const configResponse = await fetchConfig({ body: null });
    if (!configResponse.success) {
      if (configResponse.error?.logout) {
        await logout();
      }
      return handleHttpError(configResponse, toast);
    }
    setUser(configResponse.data.user);
    setBrands(configResponse.data.brands);

    toast({
      content: `Welcome back, ${
        configResponse.data.user.name || configResponse.data.user.email
      } 👋`,
      toastType: "success",
    });

    navigate("/admin", { replace: true });
  }

  if (otpSent) {
    return (
      <div className="flex justify-center items-center">
        <OTPVerificationForm
          title="Enter mail verification code"
          description={`enter the code sent to ${authenticationState?.email}`}
          caller={onVerify}
          loading={verifying || fetchingConfig}
          onResend={onResend}
          canResend={authenticationState?.otp?.canResend ?? false}
          resendIn={authenticationState?.otp?.resendIn ?? 30}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-12 w-full">
      <h1 className="w-full font-semibold text-3xl">Welcome back!</h1>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-5 w-full"
        >
          <FormInput
            name="email"
            label="Enter your email"
            placeholder="xyz@workmail.com"
            form={form}
            min={1}
            type="email"
          />
          <div className="flex flex-col items-center space-y-8">
            <Button
              disabled={sendingOtp}
              className="bg-primarybuttonbg py-8 border border-primarybuttonborder w-full font-semibold text-lg text-primarybuttontext"
              type="submit"
            >
              {sendingOtp ? <Spinner width={20} /> : "Get OTP"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
